<script setup lang="ts">
import {computed, reactive, ref, watch} from "vue";
import {storeToRefs} from "pinia";
import {useClipboard} from "@vueuse/core";
import {
    Check,
    ChevronDown,
    Copy,
    FlaskConical,
    Globe,
    Loader2,
    Minus,
    Plus,
    Send,
    SlidersHorizontal,
    Store,
    Tag,
    TriangleAlert,
    User,
    Users
} from "@lucide/vue";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle
} from "@/core/components/ui/alert-dialog";
import {Button} from "@/core/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle
} from "@/core/components/ui/dialog";
import {Input} from "@/core/components/ui/input";
import {Label} from "@/core/components/ui/label";
import {Tabs, TabsList, TabsTrigger} from "@/core/components/ui/tabs";
import AgendaPatientSearch, {SelectedPatient} from "@/agenda/components/AgendaPatientSearch.vue";
import HabilitatedBadge from "@/agenda/components/HabilitatedBadge.vue";
import BookingLinkLabPicker from "@/agenda/components/booking-link/BookingLinkLabPicker.vue";
import BookingLinkPresetList, {BookingLinkPresetRow} from "@/agenda/components/booking-link/BookingLinkPresetList.vue";
import useAgendaOperators from "@/agenda/composables/useAgendaOperators";
import useAppointmentTypes from "@/agenda/composables/useAppointmentTypes";
import useBookingLinkPresets from "@/agenda/composables/useBookingLinkPresets";
import useOperatorChoices from "@/agenda/composables/useOperatorChoices";
import useTeamSchedule from "@/team/composables/useTeamSchedule";
import {
    BookingLinkDraft,
    BookingLinkLaboratory,
    BookingLinkMode,
    DEFAULT_HORIZON_DAYS,
    DEFAULT_MIN_NOTICE_MINUTES,
    draftFromPreset,
    formatHorizon,
    formatNotice,
    HORIZON_LADDER,
    isValidFrenchMobile,
    normalizeFrenchMobile,
    NOTICE_LADDER,
    stepOnLadder
} from "@/agenda/lib/bookingLink";
import {bookingLinkPresetApi} from "@/container";
import {BookingLinkPreset} from "@/core/http/resources/BookingLinkPresetResource";
import {apiErrorMessage, apiErrorStatus, isAuthorizationError} from "@/core/http/apiErrorMessage";
import {JOB_ROLES} from "@/team/planning";
import {useSystemStore} from "@/stores/system.store";
import {useToastStore} from "@/stores/toast.store";

const props = defineProps<{
    /**
     * Patient the link is for, when the modal opens from their file: they land
     * in the SMS field with their number, as if picked in the search.
     */
    initialPatient?: SelectedPatient | null;
}>();

const open = defineModel<boolean>('open', {required: true});

const toastStore = useToastStore();
const {environment} = storeToRefs(useSystemStore());


const {operators} = useAgendaOperators();
const {schedulesByAccessId, organizationId} = useTeamSchedule();
const {data: types} = useAppointmentTypes();
/** The hidden templates too: a suspended link names the type it still imposes. */
const {data: typesWithHidden} = useAppointmentTypes({withHidden: true});
const {data: presets, error: presetsError, isLoading: presetsLoading, refresh: refreshPresets} = useBookingLinkPresets(() => organizationId.value);

/** Types the patient may book online; the others never enter a link. */
const bookableTypes = computed(() => (types.value ?? []).filter((type) => type.is_bookable_online));

/**
 * Why a link imposing this type is suspended — the type hidden or deleted
 * since — or null when it is not. The health server answers such a link as
 * an unknown one until the type is offered again. Null too while the types
 * load, so nothing flashes as suspended.
 */
const suspension = (typeId: number | null): {reason: string; hiddenName: string | null} | null => {
    if (typeId === null || !types.value || types.value.some((type) => type.id === typeId)) {
        return null;
    }

    const hidden = typesWithHidden.value?.find((type) => type.id === typeId);

    return hidden
        ? {reason: `« ${hidden.name} » masqué`, hiddenName: hidden.name}
        : {reason: 'type supprimé', hiddenName: null};
};

/** Role label of each member, purely informative in the roster rows. */
const roleByAccessId = computed(() => {
    const map = new Map<number, string>();

    operators.value.forEach((operator) => {
        const role = schedulesByAccessId.value.get(operator.accessId)?.job_role;
        const label = JOB_ROLES.find((option) => option.value === role)?.label;

        if (label) {
            map.set(operator.accessId, label);
        }
    });

    return map;
});

/** Laboratories the team is referent for, offered as picker shortcuts. */
const labSuggestions = computed<BookingLinkLaboratory[]>(() => {
    const map = new Map<number, BookingLinkLaboratory>();

    schedulesByAccessId.value.forEach((schedule) => {
        schedule.referent_laboratories.forEach((laboratory) => map.set(laboratory.id, laboratory));
    });

    return [...map.values()];
});

const tab = ref<'presets' | 'new'>('presets');

/**
 * The extra settings are folded away: a pharmacy-wide link with no imposed
 * reason is the common case, and everything in there refines it rather than
 * being needed to produce it.
 */
const extrasOpen = ref(false);

/**
 * The persisted row the drafted link currently stands for: a template picked
 * in the list, or the code minted on the last copy or send. Null once the
 * draft has diverged from every known row.
 *
 * It is the single source of the URL and of the code the SMS carries — there
 * is no client-composed address any more.
 */
const current = ref<BookingLinkPreset | null>(null);
const editedPresetId = ref<number | null>(null);

/**
 * Bumped by every hand edit, so a code minted for options the pharmacist has
 * since changed is discarded when it lands rather than published.
 */
let draftRevision = 0;
const minting = ref(false);

/*
 * State of the « save as template » box. Declared here rather than beside
 * `savePreset`: `applyPreset` resets it, and a ref used before its
 * declaration line is a temporal dead zone the day a watcher runs at setup.
 */
const saveOpen = ref(false);
const saveName = ref('');
const savingPreset = ref(false);

const draft = reactive<BookingLinkDraft>({
    mode: 'any',
    operatorIds: [],
    combine: 'any',
    appointmentTypeId: null,
    laboratory: null,
    minNoticeMinutes: DEFAULT_MIN_NOTICE_MINUTES,
    horizonDays: DEFAULT_HORIZON_DAYS
});

const draftSuspension = computed(() => suspension(draft.appointmentTypeId));

const detachFromPreset = () => {
    current.value = null;
    draftRevision += 1;
};

/**
 * The whole team, exactly as the « Mon équipe » screen lists it — that screen
 * shows everything the endpoint returns, so this one does too. Every member
 * can be designated, in the team's order for the drafted type and laboratory,
 * and nobody is greyed out.
 */
const {choices: roster, preferred} = useOperatorChoices(() => ({
    appointmentTypeId: draft.appointmentTypeId,
    laboratoryId: draft.laboratory?.id ?? null
}));

/**
 * Who a mode switch lands on: the pharmacist holding the session, since they
 * are usually the one being booked, unless the team's order puts somebody
 * ahead of them.
 */
const preferredOperatorId = (): number | null => preferred(environment.value?.id ?? null);

/**
 * Keep the selection consistent with the mode after a change. A filter change
 * never takes anybody out: habilitations only reorder the roster, so the
 * members already picked stay picked.
 */
const reconcileSelection = () => {
    if (draft.mode === 'any') {
        draft.operatorIds = [];

        return;
    }

    const kept = draft.operatorIds.filter((id) => operators.value.some((operator) => operator.accessId === id));
    const preferred = preferredOperatorId();

    if (!kept.length && preferred === null) {
        draft.mode = 'any';
        draft.operatorIds = [];

        return;
    }

    if (draft.mode === 'one') {
        draft.operatorIds = kept.length ? [kept[0]] : [preferred as number];

        return;
    }

    draft.operatorIds = kept.length ? kept : [preferred as number];
};

const setMode = (mode: BookingLinkMode) => {
    draft.mode = mode;
    detachFromPreset();
    reconcileSelection();
};

const setType = (appointmentTypeId: number | null) => {
    draft.appointmentTypeId = appointmentTypeId;
    detachFromPreset();
    reconcileSelection();
};

const setNotice = (minutes: number) => {
    draft.minNoticeMinutes = minutes;
    detachFromPreset();
};

const setHorizon = (days: number) => {
    draft.horizonDays = days;
    detachFromPreset();
};

/** Whether anything inside the fold departs from the plain default link. */
const draftHasExtras = () => draft.appointmentTypeId !== null
    || draft.laboratory !== null
    || draft.minNoticeMinutes !== DEFAULT_MIN_NOTICE_MINUTES
    || draft.horizonDays !== DEFAULT_HORIZON_DAYS;

const stepNotice = (direction: 1 | -1) => setNotice(stepOnLadder(NOTICE_LADDER, draft.minNoticeMinutes, direction));

const stepHorizon = (direction: 1 | -1) => setHorizon(stepOnLadder(HORIZON_LADDER, draft.horizonDays, direction));

/**
 * Whether a stepper can still move that way.
 *
 * A value off the ladder is never at an end: the first press snaps it back
 * onto the nearest rung, which is a change worth allowing.
 */
const canStep = (ladder: number[], current: number, direction: 1 | -1) =>
    stepOnLadder(ladder, current, direction) !== current;

/*
 * Wired to the picker rather than watched: a watcher flushes after the
 * current tick, so applying a template — which writes the laboratory then
 * marks the template as selected — would have seen the change land last and
 * detached the very selection it had just made. Going through the handler
 * keeps « the user changed it » and « the code set it » apart by
 * construction.
 */
const setLaboratory = (laboratory: BookingLinkLaboratory | null) => {
    draft.laboratory = laboratory;
    detachFromPreset();
    reconcileSelection();
};

const toggleOperator = (accessId: number) => {
    detachFromPreset();

    if (draft.mode !== 'multi') {
        draft.operatorIds = [accessId];

        return;
    }

    const index = draft.operatorIds.indexOf(accessId);

    if (index === -1) {
        draft.operatorIds = [...draft.operatorIds, accessId];
    } else if (draft.operatorIds.length > 1) {
        // The last one stays: an empty multi selection describes no link.
        draft.operatorIds = draft.operatorIds.filter((id) => id !== accessId);
    }
};

const url = computed(() => current.value?.url ?? null);

/**
 * Whether there is a link to show at all. On the saved tab the link belongs
 * to a template: with none picked, the URL bar and the SMS step have nothing
 * to carry and stay out of the way.
 *
 * A named row, not just any: `current` also holds the nameless code minted by
 * a copy on the other tab, and that one is never listed — showing its URL here
 * would offer a link matching none of the rows above.
 */
const linkShown = computed(() => tab.value === 'new'
    || (current.value !== null && current.value.name !== null));

const draftRequest = (name: string | null) => ({
    name,
    mode: draft.mode,
    operator_ids: draft.operatorIds,
    combine: draft.mode === 'multi' ? draft.combine : null,
    appointment_type_id: draft.appointmentTypeId,
    laboratory_id: draft.laboratory?.id ?? null,
    min_notice_minutes: draft.minNoticeMinutes,
    horizon_days: draft.horizonDays
});

/**
 * The persisted row for the draft as it stands, minting a nameless code the
 * first time it is needed.
 *
 * Null when the draft moved while the request was in flight, or when it
 * failed — the error has been shown by then. The server hands back the same
 * code for an identical draft, so copying twice leaves one row behind, not
 * two.
 */
const ensureCode = async (): Promise<BookingLinkPreset | null> => {
    if (current.value) {
        return current.value;
    }

    const currentOrganizationId = organizationId.value;

    if (!currentOrganizationId) {
        toastStore.showError({content: "Officine non identifiée : réessayez dans un instant."});

        return null;
    }

    // Already minting: the buttons are disabled meanwhile, so this only
    // guards a second programmatic call and stays silent on purpose.
    if (minting.value) {
        return null;
    }

    const revision = draftRevision;
    minting.value = true;

    try {
        const row = await bookingLinkPresetApi().create(currentOrganizationId, draftRequest(null));

        if (revision !== draftRevision) {
            return null;
        }

        current.value = row;

        return row;
    } catch (error) {
        if (!isAuthorizationError(error)) {
            toastStore.showError({content: apiErrorMessage(error)});
        }

        return null;
    } finally {
        minting.value = false;
    }
};

/* ------------------------------- copy -------------------------------- */

const {copy, copied} = useClipboard({legacy: true});

const copyLink = async () => {
    const row = await ensureCode();

    if (!row) {
        return;
    }

    if (!row.url) {
        toastStore.showError({content: "Le lien public n'est pas encore disponible sur cet environnement."});

        return;
    }

    await copy(row.url);
    toastStore.showSuccess({content: 'Lien copié dans le presse-papiers.'});
};

/* ------------------------------ presets ------------------------------ */

const applyPreset = (preset: BookingLinkPreset) => {
    const rebuilt = draftFromPreset(
        preset,
        operators.value.map((operator) => operator.accessId)
    );

    draft.mode = rebuilt.mode;
    draft.operatorIds = rebuilt.operatorIds;
    draft.combine = rebuilt.combine;
    draft.appointmentTypeId = rebuilt.appointmentTypeId;
    draft.laboratory = rebuilt.laboratory;
    draft.minNoticeMinutes = rebuilt.minNoticeMinutes;
    draft.horizonDays = rebuilt.horizonDays;
    // Unfolded when the template actually sets something in there: hiding a
    // configuration the pharmacist just picked would misrepresent the link.
    extrasOpen.value = extrasOpen.value || draftHasExtras();
    current.value = preset;
    draftRevision += 1;
    /*
     * Applying a template ends any edition in progress — id, open save box and
     * prefilled name alike. Keeping the previous `editedPresetId` meant a later
     * save updated the template the user had stopped editing, with the options
     * of the one they had just applied; keeping its name meant creating a
     * duplicate of it. `editPreset` re-sets all three right after calling this.
     */
    editedPresetId.value = null;
    saveOpen.value = false;
    saveName.value = '';
};

const presetRows = computed<BookingLinkPresetRow[]>(() => {
    return (presets.value ?? []).map((preset) => {
        const names = preset.operator_ids
            .map((id) => operators.value.find((operator) => operator.accessId === id)?.name)
            .filter((name): name is string => !!name);
        const who = preset.mode === 'any' || !names.length ? 'La pharmacie' : names.join(' ou ');
        const type = bookableTypes.value.find((item) => item.id === preset.appointment_type_id);
        const suspended = suspension(preset.appointment_type_id);
        const parts = [who, suspended ? `Suspendu, ${suspended.reason}` : type ? `${type.name} · ${type.duration} min` : 'Type libre'];

        if (preset.laboratory) {
            parts.push(preset.laboratory.name);
        }

        /*
         * Defaulted rather than read straight: Connect ships continuously
         * while the API deploys on its own pace, and the booking window only
         * exists there since the codes were introduced. Without this the row
         * would read « undefined min avant · NaN mois » against an API one
         * release behind.
         */
        parts.push([
            formatNotice(preset.min_notice_minutes ?? DEFAULT_MIN_NOTICE_MINUTES),
            formatHorizon(preset.horizon_days ?? DEFAULT_HORIZON_DAYS)
        ].join(' · '));

        return {preset, subtitle: parts.join(' · '), suspended: suspended !== null};
    });
});

const editPreset = (preset: BookingLinkPreset) => {
    applyPreset(preset);
    editedPresetId.value = preset.id;
    saveName.value = preset.name ?? '';
    saveOpen.value = true;
    tab.value = 'new';
};

/* --------------------------- save as preset --------------------------- */

const savePreset = async () => {
    const name = saveName.value.trim();
    const currentOrganizationId = organizationId.value;

    if (!name || !currentOrganizationId || savingPreset.value) {
        return;
    }

    savingPreset.value = true;

    const request = draftRequest(name);

    /*
     * A code already minted by a copy is named rather than duplicated: the
     * links already in the pharmacist's clipboard keep working, and the row
     * simply joins the saved list.
     */
    const patched = editedPresetId.value
        ?? (current.value && current.value.name === null ? current.value.id : null);

    try {
        current.value = patched
            ? await bookingLinkPresetApi().update(patched, request)
            : await bookingLinkPresetApi().create(currentOrganizationId, request);

        await refreshPresets();
        toastStore.showSuccess({content: `« ${name} » est prêt à diffuser depuis les liens pré-établis.`});
        saveOpen.value = false;
        saveName.value = '';
        editedPresetId.value = null;
    } catch (error) {
        if (!isAuthorizationError(error)) {
            toastStore.showError({content: apiErrorMessage(error)});
        }
    } finally {
        savingPreset.value = false;
    }
};

/* ------------------------------ deletion ------------------------------ */

/**
 * Separate from the dialog's open state: AlertDialogAction closes the dialog
 * before the click handler runs.
 */
const presetToDelete = ref<BookingLinkPreset | null>(null);
const deleteDialogOpen = ref(false);
const deleting = ref(false);

const askDelete = (preset: BookingLinkPreset) => {
    presetToDelete.value = preset;
    deleteDialogOpen.value = true;
};

const confirmDelete = async () => {
    const preset = presetToDelete.value;

    if (!preset || deleting.value) {
        return;
    }

    deleting.value = true;

    try {
        await bookingLinkPresetApi().delete(preset.id);
        await refreshPresets();

        if (current.value?.id === preset.id) {
            current.value = null;
            draftRevision += 1;
        }

        // Nothing left to update: a save would PATCH a deleted id.
        if (editedPresetId.value === preset.id) {
            editedPresetId.value = null;
            saveOpen.value = false;
            saveName.value = '';
        }
    } catch (error) {
        if (!isAuthorizationError(error)) {
            toastStore.showError({content: apiErrorMessage(error)});
        }
    } finally {
        deleting.value = false;
        presetToDelete.value = null;
    }
};

/* -------------------------------- SMS --------------------------------- */

const patient = ref<SelectedPatient | null>(null);
const phone = ref('');
const sendingSms = ref(false);

/** Picking a patient fills the number; the field stays freely editable. */
watch(patient, (selected) => {
    if (selected?.phone) {
        phone.value = selected.phone;
    }
});

const phoneValid = computed(() => isValidFrenchMobile(phone.value));
const canSendSms = computed(() => phoneValid.value && !sendingSms.value && !minting.value && !draftSuspension.value);

/**
 * The patient the number provably belongs to, or null.
 *
 * Only sent when the field still holds the number the file carries: picking a
 * patient and then correcting the number would otherwise attribute the send
 * to them while it leaves for someone else — and since the server mints an
 * authenticated link from this id, that would hand a link identifying the
 * patient to a stranger's phone. A patient whose file has no number is left
 * out for the same reason: the HDS refuses such a link at exchange time
 * anyway, since `AuthenticationLinkService` re-checks the phone against the
 * customer record.
 */
const customerIdForSms = computed<number | null>(() => {
    const selected = patient.value;

    if (!selected?.phone) {
        return null;
    }

    return normalizeFrenchMobile(selected.phone) === normalizeFrenchMobile(phone.value)
        ? selected.id
        : null;
});

const sendSms = async () => {
    const currentOrganizationId = organizationId.value;

    if (!canSendSms.value || !currentOrganizationId) {
        return;
    }

    sendingSms.value = true;

    try {
        const row = await ensureCode();

        if (!row) {
            return;
        }

        /*
         * The code alone: the server reads the rules it stands for and owns
         * the patient-web host, so it composes both the URL and the wording.
         * Sending a ready-made link would let a caller have an arbitrary one
         * texted to a pharmacy's patients.
         */
        const {authenticated} = await bookingLinkPresetApi().sendSms(currentOrganizationId, {
            phone: phone.value.trim(),
            ...(customerIdForSms.value !== null ? {customer_id: customerIdForSms.value} : {}),
            code: row.code
        });
        toastStore.showSuccess({
            content: authenticated
                ? `Lien envoyé au ${phone.value.trim()} — ${patient.value?.name ?? 'le patient'} arrivera identifié.`
                : `Lien envoyé au ${phone.value.trim()}.`
        });
    } catch (error) {
        if (isAuthorizationError(error)) {
            return;
        }

        // 402 carries no validation bag, so apiErrorMessage would fall back to
        // the generic wording: the counter needs to hear it is a credit issue.
        toastStore.showError({
            content: apiErrorStatus(error) === 402
                ? 'Crédits SMS insuffisants : rechargez votre solde pour envoyer le lien.'
                : apiErrorMessage(error)
        });
    } finally {
        sendingSms.value = false;
    }
};

/* ------------------------------- reset -------------------------------- */

watch(open, (isOpen) => {
    if (!isOpen) {
        return;
    }

    // The pharmacy-wide link is the default: it is the one that always works,
    // whatever the fiches declare, and naming a collaborator is the deliberate
    // step.
    draft.mode = 'any';
    draft.operatorIds = [];
    draft.combine = 'any';
    draft.appointmentTypeId = null;
    draft.laboratory = null;
    draft.minNoticeMinutes = DEFAULT_MIN_NOTICE_MINUTES;
    draft.horizonDays = DEFAULT_HORIZON_DAYS;
    current.value = null;
    draftRevision += 1;
    extrasOpen.value = false;
    editedPresetId.value = null;
    saveOpen.value = false;
    saveName.value = '';
    patient.value = props.initialPatient ?? null;
    // Set here as well as by the patient watcher: reopening for the same
    // patient leaves the ref unchanged, and the watcher would not fire.
    phone.value = props.initialPatient?.phone ?? '';
    // The saved tab greets reopenings (the mock's default), unless it has
    // nothing to show — presets known empty, or their endpoint failing.
    tab.value = (presetsError.value && !presets.value) || (presets.value && !presets.value.length)
        ? 'new'
        : 'presets';
});

const MODES: {key: BookingLinkMode; label: string; icon: typeof User; title: string}[] = [
    {key: 'one', label: 'Un collaborateur', icon: User, title: 'Rendez-vous avec un collaborateur précis'},
    {key: 'multi', label: 'Plusieurs', icon: Users, title: 'Plusieurs collaborateurs, au choix du patient'},
    {key: 'any', label: 'La pharmacie', icon: Store, title: 'Peu importe le collaborateur'}
];


const cancelSave = () => {
    saveOpen.value = false;
    editedPresetId.value = null;
};

const initialsOf = (name: string) => {
    return name
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0]?.toUpperCase() ?? '')
        .join('');
};
</script>

<template>
  <Dialog v-model:open="open">
    <!--
      max-w-2xl: the root font-size is 14px, so rem-based widths run ~12%
      smaller than usual — max-w-lg left this dense content overflowing
      horizontally.
    -->
    <DialogContent class="max-h-[85vh] max-w-2xl overflow-y-auto">
      <DialogHeader>
        <DialogTitle>Partager un lien de rendez-vous</DialogTitle>
        <DialogDescription>
          Le patient choisit son créneau parmi les disponibilités du collaborateur.
        </DialogDescription>
      </DialogHeader>
      <!--
        min-w-0: DialogContent is a grid, and a grid item's automatic minimum
        size is its min-content width. Without it the un-breakable URL below
        stretches the dialog past max-w-2xl instead of being truncated.
      -->
      <div class="flex min-w-0 flex-col gap-4">
        <Tabs v-model="tab">
          <TabsList class="w-full">
            <TabsTrigger value="presets" class="flex-1">
              Liens pré-établis
            </TabsTrigger>
            <TabsTrigger value="new" class="flex-1">
              Nouveau lien
            </TabsTrigger>
          </TabsList>
        </Tabs>

        <BookingLinkPresetList
          v-if="tab === 'presets'"
          :rows="presetRows"
          :selected-id="current?.id ?? null"
          :loading="presetsLoading"
          :failed="!!presetsError && !presets"
          @select="applyPreset"
          @edit="editPreset"
          @remove="askDelete"
        />

        <template v-if="tab === 'new'">
          <div class="flex flex-col gap-2">
            <p class="text-sm font-medium">
              Avec qui ?
            </p>
            <div class="flex gap-2">
              <Button
                v-for="mode in MODES"
                :key="mode.key"
                type="button"
                class="min-w-0 flex-1"
                :variant="draft.mode === mode.key ? 'secondary' : 'outline'"
                :aria-pressed="draft.mode === mode.key"
                :title="mode.title"
                @click="setMode(mode.key)"
              >
                <component :is="mode.icon" class="size-4" />
                {{ mode.label }}
              </Button>
            </div>
            <p
              v-if="draft.mode === 'any'"
              class="flex items-start gap-2 rounded-lg bg-primary-lightest px-3 py-2 text-xs text-gray-700"
            >
              <Store class="mt-0.5 size-3.5 shrink-0 text-primary" />
              Le patient réserve auprès de la pharmacie : tous les créneaux où un
              collaborateur est présent sont proposés, et le rendez-vous revient
              en priorité à un collaborateur habilité.
            </p>
            <div v-if="draft.mode !== 'any' && roster.length" class="flex flex-col gap-1">
              <button
                v-for="row in roster"
                :key="row.operator.key"
                type="button"
                class="flex items-center gap-3 rounded-lg border px-3 py-2 text-left transition-colors"
                :class="draft.operatorIds.includes(row.operator.accessId)
                  ? 'border-primary bg-primary-lightest'
                  : 'border-slate-200 hover:border-slate-300'"
                @click="toggleOperator(row.operator.accessId)"
              >
                <span class="flex size-8 shrink-0 items-center justify-center rounded-full bg-primary-lightest text-xs font-semibold text-primary">
                  {{ initialsOf(row.operator.name) }}
                </span>
                <span class="min-w-0 flex-1">
                  <span class="block truncate text-sm font-medium">{{ row.operator.name }}</span>
                  <span
                    v-if="roleByAccessId.get(row.operator.accessId)"
                    class="block text-xs text-gray-500"
                  >
                    {{ roleByAccessId.get(row.operator.accessId) }}
                  </span>
                </span>
                <HabilitatedBadge v-if="row.habilitated" />
                <Check
                  class="size-4 shrink-0"
                  :class="draft.operatorIds.includes(row.operator.accessId)
                    ? 'text-primary'
                    : 'text-transparent'"
                />
              </button>
            </div>
          </div>

          <!--
            Everything in here refines a link that already works without it, so
            it is folded away: the common case is the pharmacy-wide link with
            no imposed reason.
          -->
          <div class="rounded-xl border border-slate-200">
            <button
              type="button"
              class="flex w-full items-center gap-2 px-3 py-2.5 text-left"
              :aria-expanded="extrasOpen"
              aria-controls="booking-link-extras"
              @click="extrasOpen = !extrasOpen"
            >
              <SlidersHorizontal class="size-4 shrink-0 text-primary" />
              <span class="min-w-0 flex-1 text-sm font-medium">
                Paramètres supplémentaires
                <span class="font-normal text-gray-500">(optionnel)</span>
              </span>
              <ChevronDown
                class="size-4 shrink-0 text-gray-500 transition-transform"
                :class="extrasOpen && 'rotate-180'"
              />
            </button>

            <div v-if="extrasOpen" id="booking-link-extras" class="flex flex-col gap-4 border-t border-slate-200 p-3">
              <div class="flex flex-col gap-2">
                <p class="flex items-center gap-1.5 text-xs font-semibold tracking-wide text-primary uppercase">
                  <Tag class="size-3.5" />
                  Type de rendez-vous
                </p>
                <div class="flex flex-wrap gap-1.5">
                  <Button
                    type="button"
                    size="sm"
                    :variant="draft.appointmentTypeId === null ? 'secondary' : 'outline'"
                    @click="setType(null)"
                  >
                    Aucun type imposé · le patient choisit
                  </Button>
                  <Button
                    v-for="type in bookableTypes"
                    :key="type.id"
                    type="button"
                    size="sm"
                    :variant="draft.appointmentTypeId === type.id ? 'secondary' : 'outline'"
                    @click="setType(type.id)"
                  >
                    {{ type.name }} · {{ type.duration }} min
                  </Button>
                </div>
                <p v-if="!bookableTypes.length" class="text-xs text-gray-500">
                  Aucun type n'est réservable en ligne pour l'instant — activez
                  « Réservable en ligne » dans la configuration des RDV.
                </p>
              </div>

              <div class="grid gap-2 sm:grid-cols-2">
                <div class="flex min-w-0 flex-col gap-1.5 rounded-lg border border-slate-200 px-3 py-2.5">
                  <span class="text-xs font-semibold tracking-wide text-gray-500 uppercase">
                    Délai minimum avant un créneau
                  </span>
                  <div class="flex items-center gap-2">
                    <Button
                      type="button"
                      size="icon"
                      variant="outline"
                      class="size-7 shrink-0"
                      aria-label="Réduire le délai minimum"
                      :disabled="!canStep(NOTICE_LADDER, draft.minNoticeMinutes, -1)"
                      @click="stepNotice(-1)"
                    >
                      <Minus class="size-3.5" />
                    </Button>
                    <span class="min-w-0 flex-1 text-center text-sm font-semibold text-primary" aria-live="polite">
                      {{ formatNotice(draft.minNoticeMinutes) }}
                    </span>
                    <Button
                      type="button"
                      size="icon"
                      variant="outline"
                      class="size-7 shrink-0"
                      aria-label="Augmenter le délai minimum"
                      :disabled="!canStep(NOTICE_LADDER, draft.minNoticeMinutes, 1)"
                      @click="stepNotice(1)"
                    >
                      <Plus class="size-3.5" />
                    </Button>
                  </div>
                  <span class="text-xs text-gray-500">
                    Le patient ne peut pas réserver en dessous de ce délai.
                  </span>
                </div>

                <div class="flex min-w-0 flex-col gap-1.5 rounded-lg border border-slate-200 px-3 py-2.5">
                  <span class="text-xs font-semibold tracking-wide text-gray-500 uppercase">
                    Réservable jusqu'à
                  </span>
                  <div class="flex items-center gap-2">
                    <Button
                      type="button"
                      size="icon"
                      variant="outline"
                      class="size-7 shrink-0"
                      aria-label="Réserver moins loin à l'avance"
                      :disabled="!canStep(HORIZON_LADDER, draft.horizonDays, -1)"
                      @click="stepHorizon(-1)"
                    >
                      <Minus class="size-3.5" />
                    </Button>
                    <span class="min-w-0 flex-1 text-center text-sm font-semibold text-primary" aria-live="polite">
                      {{ formatHorizon(draft.horizonDays) }}
                    </span>
                    <Button
                      type="button"
                      size="icon"
                      variant="outline"
                      class="size-7 shrink-0"
                      aria-label="Réserver plus loin à l'avance"
                      :disabled="!canStep(HORIZON_LADDER, draft.horizonDays, 1)"
                      @click="stepHorizon(1)"
                    >
                      <Plus class="size-3.5" />
                    </Button>
                  </div>
                  <span class="text-xs text-gray-500">
                    Le patient ne peut pas réserver au-delà.
                  </span>
                </div>
              </div>

              <div class="flex flex-col gap-2">
                <p class="flex items-center gap-1.5 text-xs font-semibold tracking-wide text-primary uppercase">
                  <FlaskConical class="size-3.5" />
                  Laboratoire partenaire
                </p>
                <BookingLinkLabPicker
                  :model-value="draft.laboratory"
                  :suggestions="labSuggestions"
                  @update:model-value="setLaboratory"
                />
              </div>
            </div>
          </div>

        </template>

        <!--
          On the saved tab the link belongs to a template: with none picked
          there is nothing to show, and printing the drafted link there would
          offer a URL matching none of the rows listed above.
        -->
        <!--
          A suspended link cannot be copied or sent: the patient would land on
          an unknown link. It comes back once its type is offered again.
        -->
        <p
          v-if="linkShown && draftSuspension"
          class="flex items-start gap-1.5 rounded-lg bg-orange/20 px-2.5 py-2 text-xs leading-snug text-gray-dark"
        >
          <TriangleAlert class="mt-px size-3.5 shrink-0 text-orange" />
          <span>
            Ce lien est suspendu ({{ draftSuspension.reason }}) : le patient ne peut plus réserver avec.
            Choisissez un autre type{{ draftSuspension.hiddenName ? `, ou réaffichez « ${draftSuspension.hiddenName} » dans la configuration des RDV` : '' }}.
          </span>
        </p>
        <div
          v-if="linkShown"
          class="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2"
        >
          <Globe class="size-4 shrink-0 text-gray-400" />
          <p class="min-w-0 flex-1 truncate font-mono text-xs" :title="url ?? undefined">
            <!--
              The address is the server's to mint: until the pharmacist copies
              or sends, there is no code and nothing to print here.
            -->
            {{ url ?? 'Le lien est généré à la copie ou à l’envoi.' }}
          </p>
          <Button
            type="button"
            size="sm"
            variant="outline"
            class="shrink-0"
            :disabled="minting || !!draftSuspension"
            @click="copyLink"
          >
            <Loader2 v-if="minting" class="size-3.5 animate-spin" />
            <Check v-else-if="copied" class="size-3.5 text-green" />
            <Copy v-else class="size-3.5" />
            {{ copied ? 'Copié' : 'Copier' }}
          </Button>
        </div>

        <div v-if="tab === 'new'" class="rounded-lg border border-slate-200 p-3">
          <div v-if="saveOpen" class="flex items-center gap-2">
            <Input
              v-model="saveName"
              class="min-w-0 flex-1"
              placeholder="Nom du lien (ex. Vaccination grippe — Claire)"
              @keydown.enter.prevent="savePreset"
            />
            <Button type="button" class="bg-primary" :disabled="!saveName.trim() || savingPreset" @click="savePreset">
              <Loader2 v-if="savingPreset" class="size-4 animate-spin" />
              Enregistrer
            </Button>
            <Button type="button" variant="ghost" @click="cancelSave">
              Annuler
            </Button>
          </div>
          <div v-else class="flex items-center gap-3">
            <p class="flex-1 text-xs text-gray-600">
              <b>Réutiliser ce lien plus tard ?</b><br>
              Enregistrez-le comme modèle : il sera diffusable en un clic depuis
              « Liens pré-établis ».
            </p>
            <Button type="button" variant="outline" size="sm" @click="saveOpen = true">
              Enregistrer
            </Button>
          </div>
        </div>

        <!-- No link to send either, for the same reason. -->
        <div v-if="linkShown" class="flex flex-col gap-2">
          <p class="text-sm font-medium">
            Envoyer par SMS
          </p>
          <div class="flex flex-col gap-1.5">
            <Label>Patient</Label>
            <AgendaPatientSearch v-model="patient" />
          </div>
          <div class="flex items-end gap-2">
            <div class="flex min-w-0 flex-1 flex-col gap-1.5">
              <Label for="booking-link-phone">Téléphone mobile</Label>
              <Input
                id="booking-link-phone"
                v-model="phone"
                type="tel"
                placeholder="06 12 34 56 78"
              />
            </div>
            <Button type="button" class="bg-primary" :disabled="!canSendSms" @click="sendSms">
              <Loader2 v-if="sendingSms" class="size-4 animate-spin" />
              <Send v-else class="size-4" />
              Envoyer
            </Button>
          </div>
          <!--
            No preview of the message: the server composes its wording, and
            mirroring it here would drift the day either side is reworded.
          -->
          <p class="text-xs text-gray-500">
            Le patient reçoit un message de la pharmacie avec le lien de réservation.
          </p>
        </div>
      </div>
    </DialogContent>
  </Dialog>
  <AlertDialog v-model:open="deleteDialogOpen">
    <AlertDialogContent class="z-[60]">
      <AlertDialogHeader>
        <AlertDialogTitle>Supprimer « {{ presetToDelete?.name }} » ?</AlertDialogTitle>
        <AlertDialogDescription>
          Le modèle disparaît des liens pré-établis, et les liens déjà diffusés
          indiqueront au patient qu'ils ne sont plus actifs.
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel>Annuler</AlertDialogCancel>
        <AlertDialogAction class="bg-red" :disabled="deleting" @click="confirmDelete">
          Supprimer
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
</template>
